import React, { useState } from 'react';
import { X, CreditCard, Building, ExternalLink, Wallet, CheckCircle } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';
import Modal from './ui/Modal';

const PaymentLinkModal = ({ 
  onClose, 
  onSuccess, 
  type = 'wallet', // 'wallet' or 'invoice'
  amount: initialAmount = '50',
  invoiceId = null,
  invoiceNumber = null 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [amount, setAmount] = useState(initialAmount);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState(null);

  // Helper function to format date to British format (DD-MM-YYYY)
  const formatDateToBritish = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    // UTC getters: a date-only value shifts a day back in negative-offset timezones.
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      if (type === 'wallet') {
        await handleWalletTopUp();
      } else if (type === 'invoice') {
        await handleInvoicePayment();
      }
    } catch (error) {
      setError(error.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleWalletTopUp = async () => {
    try {
        if (paymentMethod === 'gocardless') {
          // Check if user has GoCardless mandates
          const mandates = await ClientAuthService.getMandates();
          if (!mandates.data || mandates.data.length === 0) {
            // Redirect to mandate setup
            const successUrl = `${window.location.origin}/client/mandate-success`;
            const setup = await ClientAuthService.createMandateSetup(successUrl);
            window.location.href = setup.data.redirect_url;
            return;
          }
          
          // Use the default or first mandate
          const defaultMandate = mandates.data.find(m => m.is_default_for_topup) || mandates.data[0];
          
          const response = await ClientAuthService.createWalletTopUp({
            amount: parseFloat(amount),
            payment_method: paymentMethod,
            mandate_id: defaultMandate.provider_method_id,
            use_payment_link: false // GoCardless doesn't use payment links
          });

          if (response.data.payment_id) {
            // GoCardless payment submitted successfully
            setPaymentLink({
              payment_method: 'gocardless',
              payment_id: response.data.payment_id,
              charge_date: response.data.charge_date,
              amount: response.data.amount,
              message: 'Direct Debit wallet top-up submitted successfully'
            });
          } else {
            throw new Error('Failed to process wallet top-up');
          }
        } else {
          // Regular payment link
          const response = await ClientAuthService.createWalletTopUp({
            amount: parseFloat(amount),
            payment_method: paymentMethod,
            use_payment_link: true
          });

          if (response.data.payment_link_url) {
            setPaymentLink(response.data);
          } else {
            throw new Error('Failed to create payment link');
          }
        }
    } catch (error) {
      throw error;
    }
  };

  const handleInvoicePayment = async () => {
    try {
      if (paymentMethod === 'gocardless') {
        // Check if user has GoCardless mandates
        const mandates = await ClientAuthService.getMandates();
        if (!mandates.data || mandates.data.length === 0) {
          // Redirect to mandate setup
          const successUrl = `${window.location.origin}/client/mandate-success`;
          const setup = await ClientAuthService.createMandateSetup(successUrl);
          window.location.href = setup.data.redirect_url;
          return;
        }
        
        // The server picks the mandate and charges the outstanding balance. There is
        // no payment link for Direct Debit: it is submitted and settles in days.
        const response = await ClientAuthService.collectInvoiceByDirectDebit(invoiceId);

        if (response.data?.payment_id) {
          setPaymentLink({
            payment_method: 'gocardless',
            payment_id: response.data.payment_id,
            charge_date: response.data.charge_date,
            amount: response.data.amount,
            message: 'Direct Debit payment submitted successfully'
          });
        } else {
          throw new Error('Failed to submit Direct Debit payment');
        }
      } else {
        const response = await ClientAuthService.payInvoice({
          invoiceId: invoiceId,
          provider: paymentMethod,
          amount: parseFloat(amount)
        });

        if (response.data.payment_link_url) {
          setPaymentLink(response.data);
        } else {
          throw new Error('Failed to create payment link');
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const openPaymentLink = () => {
    if (paymentLink?.payment_link_url) {
      window.open(paymentLink.payment_link_url, '_blank');
      onClose();
    }
  };

  const title = type === 'wallet' ? 'Top Up Wallet' : `Pay Invoice ${invoiceNumber || ''}`;
  const description = type === 'wallet' 
    ? 'Add funds to your wallet'
    : `Pay invoice ${invoiceNumber || invoiceId}`;

  // Get appropriate button text based on payment method
  const getButtonText = () => {
    if (processing) {
      return paymentMethod === 'gocardless' ? 'Submitting Payment...' : 'Creating Payment Link...';
    }
    return paymentMethod === 'gocardless' ? 'Submit Payment' : 'Create Payment Link';
  };

  const getButtonIcon = () => {
    return paymentMethod === 'gocardless' ? <CheckCircle size={16} className="mr-1" /> : <ExternalLink size={16} className="mr-1" />;
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <>
          {type === 'wallet' ? (
            <Wallet className="mr-2" size={20} />
          ) : (
            <CreditCard className="mr-2" size={20} />
          )}
          {title}
        </>
      }
      size="default"
    >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                {error}
              </div>
            )}

            {paymentLink ? (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded mb-4">
                  {paymentLink.payment_method === 'gocardless' ? (
                    <>
                      <h6 className="mb-3 font-semibold">{type === 'wallet' ? 'Wallet Top-up Submitted' : 'Direct Debit Payment Submitted'}</h6>
                      <p className="mb-3">
                        Your Direct Debit {type === 'wallet' ? 'wallet top-up' : 'payment'} has been submitted successfully.
                      </p>
                      <div className="text-left">
                        <p><strong>Payment Details:</strong></p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Payment ID: {paymentLink.payment_id}</li>
                          <li>Amount: £{paymentLink.amount?.toFixed(2) || amount}</li>
                          <li>Collection Date: {formatDateToBritish(paymentLink.charge_date) || 'Within 3-5 business days'}</li>
                        </ul>
                        <p className="text-gray-500 text-sm mt-3 flex items-center">
                          <Wallet size={16} className="mr-1" />
                          Direct Debit payments are typically collected from your bank account within 3-5 business days.
                        </p>
                      </div>
                      <button 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 mt-3"
                        onClick={onClose}
                      >
                        Close
                      </button>
                    </>
                  ) : (
                    <>
                      <h6 className="mb-3 font-semibold">Payment Link Created</h6>
                      <p className="mb-3">
                        Click the button below to open the secure payment page where you can complete your payment.
                      </p>
                      <button 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md text-lg transition duration-200 flex items-center justify-center mx-auto"
                        onClick={openPaymentLink}
                      >
                        <ExternalLink className="mr-2" size={20} />
                        Open Payment Page
                      </button>
                    </>
                  )}
                </div>
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                  <small>
                    <strong>Amount:</strong> £{amount}<br />
                    <strong>Payment Method:</strong> {paymentMethod === 'stripe' ? 'Card Payment' : 'Direct Debit'}<br />
                    <strong>Description:</strong> {description}
                  </small>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      className={`w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        type === 'invoice' ? 'bg-gray-50' : 'bg-white'
                      }`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min="1"
                      step="0.01"
                      readOnly={type === 'invoice'}
                    />
                  </div>
                </div>

                {/* Preset Amounts for Wallet Top-up */}
                {type === 'wallet' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quick Amounts</label>
                    <div className="flex flex-wrap gap-2">
                      {[10, 25, 50, 100, 250, 500].map(preset => (
                        <button
                          key={preset}
                          type="button"
                          className={`px-3 py-1 text-sm font-medium rounded transition duration-200 ${
                            amount === preset.toString() 
                              ? 'bg-blue-600 text-white' 
                              : 'border border-blue-600 text-blue-600 hover:bg-blue-50'
                          }`}
                          onClick={() => setAmount(preset.toString())}
                        >
                          £{preset}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-start p-3 border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 transition duration-200">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="stripe"
                          checked={paymentMethod === 'stripe'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="flex items-center font-medium">
                            <CreditCard className="mr-2" size={16} />
                            Card Payment
                          </div>
                          <small className="text-gray-500">Instant</small>
                        </div>
                      </label>
                    </div>
                    {/* <div>
                      <label className="flex items-start p-3 border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 transition duration-200">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="gocardless"
                          checked={paymentMethod === 'gocardless'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="flex items-center font-medium">
                            <Building className="mr-2" size={16} />
                            Direct Debit
                          </div>
                          <small className="text-gray-500">3 working days</small>
                        </div>
                      </label>
                    </div> */}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
                  <small>
                    <strong>Secure Payment:</strong> 
                    {paymentMethod === 'stripe' && 
                      ' You\'ll be redirected to a secure payment page hosted by Stripe where you can safely enter your payment details.'
                    }
                    {paymentMethod === 'gocardless' && 
                      ' You\'ll be redirected to GoCardless to set up or use your Direct Debit mandate. Payment will be collected within 3 working days.'
                    }
                  </small>
                </div>

                <div className="flex justify-between">
                  <button 
                    type="button" 
                    className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md transition duration-200" 
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={processing}
                    onClick={handleSubmit}
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {getButtonText()}
                      </>
                    ) : (
                      <>
                        {getButtonIcon()}
                        {getButtonText()}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
    </Modal>
  );
};

export default PaymentLinkModal;